import { codeExample, defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebmoduleRemoveEvent } from '../../events/webmodule-remove.js';
import type WebmoduleButton from '../../components/button/button.js';
import type WebmoduleTab from '../../components/tab/tab.js';
import type WebmoduleTabGroup from '../../components/tab-group/tab-group.js';
import type WebmoduleTabPanel from '../../components/tab-panel/tab-panel.js';

/** Closable tabs: removes the tab + panel and activates a neighbor. */
class DemoTabsClosable extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  render() {
    return html`
      <webmodule-tab-group @webmodule-remove=${(e: WebmoduleRemoveEvent) => this.removeTab(e)}>
        <webmodule-tab slot="nav" panel="general" active>General</webmodule-tab>
        <webmodule-tab slot="nav" panel="pricing" closable>Pricing</webmodule-tab>
        <webmodule-tab slot="nav" panel="notes" closable>Notes</webmodule-tab>

        <webmodule-tab-panel name="general"
          >The first tab stays put; the others carry a close button.</webmodule-tab-panel
        >
        <webmodule-tab-panel name="pricing">
          Closing a tab emits <code>webmodule-remove</code> on the group - the host removes the tab and panel.
        </webmodule-tab-panel>
        <webmodule-tab-panel name="notes">Close me too.</webmodule-tab-panel>
      </webmodule-tab-group>
    `;
  }

  private removeTab(e: WebmoduleRemoveEvent) {
    const tab = e.target as WebmoduleTab;
    const group = this.shadowRoot!.querySelector<WebmoduleTabGroup>('webmodule-tab-group');
    const panel = group?.querySelector<WebmoduleTabPanel>(`webmodule-tab-panel[name='${tab.panel}']`);

    if (tab.active && tab.previousElementSibling) {
      group?.show((tab.previousElementSibling as WebmoduleTab).panel);
    }

    tab.remove();
    panel?.remove();
  }
}

/** Placement switcher for the tab strip. */
class DemoTabsPlacement extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }

      webmodule-tab-group {
        margin-bottom: var(--webmodule-spacing-large);
      }
    `
  ];

  @state() private placement: 'top' | 'bottom' | 'start' | 'end' = 'top';

  render() {
    return html`
      <webmodule-tab-group placement=${this.placement}>
        <webmodule-tab slot="nav" panel="p1" active>Tab 1</webmodule-tab>
        <webmodule-tab slot="nav" panel="p2">Tab 2</webmodule-tab>

        <webmodule-tab-panel name="p1">The strip sits at placement="${this.placement}".</webmodule-tab-panel>
        <webmodule-tab-panel name="p2">Tab 2 panel.</webmodule-tab-panel>
      </webmodule-tab-group>

      <webmodule-button-group size="small" @click=${(e: PointerEvent) => this.changePlacement(e)}>
        <webmodule-button variant="primary" value="top">top</webmodule-button>
        <webmodule-button variant="primary" value="bottom">bottom</webmodule-button>
        <webmodule-button variant="primary" value="start">start</webmodule-button>
        <webmodule-button variant="primary" value="end">end</webmodule-button>
      </webmodule-button-group>
    `;
  }

  private changePlacement(e: PointerEvent) {
    const value = (e.target as WebmoduleButton).value as 'top' | 'bottom' | 'start' | 'end' | undefined;

    if (value) {
      this.placement = value;
    }
  }
}

export function registerNavigationDemos() {
  defineDemoElement('demo-tabs-closable', DemoTabsClosable);
  defineDemoElement('demo-tabs-placement', DemoTabsPlacement);
}

export const tabsSection: DemoSection = {
  id: 'tabs',
  title: 'Tabs',
  tags: ['webmodule-tab-group', 'webmodule-tab', 'webmodule-tab-panel'],
  summary:
    'Tab group coordinates a strip of tabs with their panels: roles, keyboard navigation, scroll controls and the ' +
    'active indicator. The variant attribute switches the whole family between the classic folder look and outlined ' +
    'chips with a gliding pill.',
  render: () => html`
    ${codeExample({
      label: 'Variant: classic vs chip',
      description: html`<code>variant</code> lives on the group and is stamped onto the slotted tabs, so the strip can
        never mix styles. Classic (the default) welds filled folder tabs to the content sheet; chip renders outlined
        chips with a solid pill gliding behind the active tab.`,
      code: `
        <webmodule-tab-group variant="classic">
          <webmodule-tab slot="nav" panel="one" active>Quotes</webmodule-tab>
          <webmodule-tab slot="nav" panel="two">Orders</webmodule-tab>
          <webmodule-tab slot="nav" panel="three">Invoices</webmodule-tab>
          <webmodule-tab-panel name="one">The classic folder-tab look (default).</webmodule-tab-panel>
          <webmodule-tab-panel name="two">Orders panel.</webmodule-tab-panel>
          <webmodule-tab-panel name="three">Invoices panel.</webmodule-tab-panel>
        </webmodule-tab-group>
        <br />
        <webmodule-tab-group variant="chip">
          <webmodule-tab slot="nav" panel="one" active>Quotes</webmodule-tab>
          <webmodule-tab slot="nav" panel="two">Orders</webmodule-tab>
          <webmodule-tab slot="nav" panel="three">Invoices</webmodule-tab>
          <webmodule-tab-panel name="one">Chips with the pill gliding behind the active tab.</webmodule-tab-panel>
          <webmodule-tab-panel name="two">Orders panel.</webmodule-tab-panel>
          <webmodule-tab-panel name="three">Invoices panel.</webmodule-tab-panel>
        </webmodule-tab-group>`
    })}

    <demo-example
      label="Closable tabs"
      .code=${stripIndent(`
        <webmodule-tab-group @webmodule-remove=\${e => removeTabAndPanel(e)}>
          <webmodule-tab slot="nav" panel="pricing" closable>Pricing</webmodule-tab>
          ...
        </webmodule-tab-group>`)}
    >
      <demo-tabs-closable></demo-tabs-closable>
    </demo-example>

    ${codeExample({
      label: 'Scrolling strip',
      description: 'When tabs overflow their container, scroll buttons appear automatically.',
      code: `
        <div style="max-width: 380px;">
          <webmodule-tab-group>
            <webmodule-tab slot="nav" panel="t1" active>Tab 1</webmodule-tab>
            <webmodule-tab slot="nav" panel="t2">Tab 2</webmodule-tab>
            <webmodule-tab slot="nav" panel="t3">Tab 3</webmodule-tab>
            <webmodule-tab slot="nav" panel="t4">Tab 4</webmodule-tab>
            <webmodule-tab slot="nav" panel="t5">Tab 5</webmodule-tab>
            <webmodule-tab slot="nav" panel="t6">Tab 6</webmodule-tab>
            <webmodule-tab slot="nav" panel="t7">Tab 7</webmodule-tab>
            <webmodule-tab slot="nav" panel="t8">Tab 8</webmodule-tab>
            <webmodule-tab-panel name="t1">Overflowing strips gain scroll controls.</webmodule-tab-panel>
            <webmodule-tab-panel name="t2">Panel 2</webmodule-tab-panel>
            <webmodule-tab-panel name="t3">Panel 3</webmodule-tab-panel>
            <webmodule-tab-panel name="t4">Panel 4</webmodule-tab-panel>
            <webmodule-tab-panel name="t5">Panel 5</webmodule-tab-panel>
            <webmodule-tab-panel name="t6">Panel 6</webmodule-tab-panel>
            <webmodule-tab-panel name="t7">Panel 7</webmodule-tab-panel>
            <webmodule-tab-panel name="t8">Panel 8</webmodule-tab-panel>
          </webmodule-tab-group>
        </div>`
    })}

    <demo-example label="Placement" .code=${`<webmodule-tab-group placement="start">...</webmodule-tab-group>`}>
      <demo-tabs-placement></demo-tabs-placement>
    </demo-example>

    ${codeExample({
      label: 'Disabled tab',
      code: `
        <webmodule-tab-group>
          <webmodule-tab slot="nav" panel="a" active>Enabled</webmodule-tab>
          <webmodule-tab slot="nav" panel="b" disabled>Disabled</webmodule-tab>
          <webmodule-tab-panel name="a">Disabled tabs are skipped by pointer and keyboard.</webmodule-tab-panel>
          <webmodule-tab-panel name="b">Unreachable.</webmodule-tab-panel>
        </webmodule-tab-group>`
    })}
  `
};

export const breadcrumbSection: DemoSection = {
  id: 'breadcrumb',
  title: 'Breadcrumb',
  tags: ['webmodule-breadcrumb', 'webmodule-breadcrumb-item'],
  summary: 'Shows where the current page sits in the hierarchy, with a customizable separator.',
  render: () => html`
    ${codeExample({
      label: 'Basics',
      code: `
        <webmodule-breadcrumb>
          <webmodule-breadcrumb-item>Catalog</webmodule-breadcrumb-item>
          <webmodule-breadcrumb-item>Windows</webmodule-breadcrumb-item>
          <webmodule-breadcrumb-item>Awning 600x900</webmodule-breadcrumb-item>
        </webmodule-breadcrumb>`
    })}
    ${codeExample({
      label: 'Custom separator and icons',
      code: `
        <webmodule-breadcrumb>
          <webmodule-icon slot="separator" name="chevron-right"></webmodule-icon>
          <webmodule-breadcrumb-item>
            <webmodule-icon slot="prefix" name="person"></webmodule-icon>
            Clients
          </webmodule-breadcrumb-item>
          <webmodule-breadcrumb-item>Acme Joinery</webmodule-breadcrumb-item>
        </webmodule-breadcrumb>`
    })}
  `
};

export const treeSection: DemoSection = {
  id: 'tree',
  title: 'Tree',
  tags: ['webmodule-tree', 'webmodule-tree-item'],
  summary: 'Hierarchical navigation with expandable, selectable items.',
  render: () => html`
    ${codeExample({
      label: 'Basics',
      code: `
        <webmodule-tree style="max-width: 360px;">
          <webmodule-tree-item expanded>
            Catalog
            <webmodule-tree-item>
              Windows
              <webmodule-tree-item>Awning</webmodule-tree-item>
              <webmodule-tree-item selected>Sliding</webmodule-tree-item>
            </webmodule-tree-item>
            <webmodule-tree-item>Doors</webmodule-tree-item>
          </webmodule-tree-item>
        </webmodule-tree>`
    })}
    ${codeExample({
      label: 'Indent guides and custom expand icons',
      description: html`Indent guides are CSS custom properties on the tree; the <code>expand-icon</code> /
        <code>collapse-icon</code> slots replace the chevrons.`,
      code: `
        <webmodule-tree style="max-width: 360px; --indent-guide-width: 1px;">
          <webmodule-icon slot="expand-icon" name="chevron-right"></webmodule-icon>
          <webmodule-icon slot="collapse-icon" name="chevron-down"></webmodule-icon>
          <webmodule-tree-item expanded>
            Projects
            <webmodule-tree-item>
              Site A
              <webmodule-tree-item>Quotes</webmodule-tree-item>
              <webmodule-tree-item>Orders</webmodule-tree-item>
            </webmodule-tree-item>
            <webmodule-tree-item>Site B</webmodule-tree-item>
          </webmodule-tree-item>
        </webmodule-tree>`
    })}
  `
};
